import { useEffect, useRef } from 'react';
import { onDisplayAlert } from '../utils/alerts/alerts';

/**
 * Flattens a Formik error object into a list of field keys, including nested
 * keys (e.g. `monday.price_per_hour`) which Formik uses for object-shaped
 * schemas. The dotted key is what we try to match against a DOM name/id.
 */
const collectErrorKeys = (errors, prefix = '') => {
  if (!errors || typeof errors !== 'object') return [];
  return Object.keys(errors).reduce((keys, key) => {
    const value = errors[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return keys.concat(collectErrorKeys(value, path));
    }
    return keys.concat(path);
  }, []);
};

const findNode = (key) => document.getElementById(key) || document.querySelector(`[name="${key}"]`);

/**
 * Shared submit-time validation feedback for Formik forms.
 *
 * On each failed submit it:
 *  - shows an info toast telling the user something is missing, and
 *  - scrolls to and focuses the first (topmost) invalid field so its error
 *    highlight is visible even when the user is at the bottom of the form.
 *
 * IMPORTANT — why this doesn't simply key on `submitCount` changing:
 * Formik increments `submitCount` and then runs (async) validation, so
 * `submitCount` can change on one render while `errors` is still empty, with
 * `errors` populating on a later render. If we only acted on the render where
 * `submitCount` changed, we'd see zero errors and bail, never firing the toast
 * even though the field-level errors then appear. Instead we remember the last
 * submitCount we actually *handled*, and act on whichever render first has both
 * a new submit attempt and a populated errors object.
 *
 * The scroll gracefully degrades: if no invalid field maps to a locatable DOM
 * node, the toast still shows so the user is never left without feedback.
 *
 * @param {object} params
 * @param {number} params.submitCount  Formik submitCount
 * @param {object} params.errors       Formik errors
 * @param {boolean} params.isSubmitting Formik isSubmitting
 * @param {string} [params.message]    Optional custom toast body
 */
export const useScrollToFirstError = ({
  submitCount,
  errors,
  isSubmitting,
  message = 'Please complete the required fields highlighted in the form.',
}) => {
  const handledSubmitCount = useRef(0);

  useEffect(() => {
    // Nothing to do until a new submit attempt occurs.
    if (submitCount <= handledSubmitCount.current) return;
    // Validation may still be resolving on this render; wait for errors to land.
    if (isSubmitting) return;

    const errorKeys = collectErrorKeys(errors);
    if (errorKeys.length === 0) return; // submit was valid (or errors not yet set)

    // Mark this submit attempt as handled so we only notify once per attempt.
    handledSubmitCount.current = submitCount;

    onDisplayAlert({
      title: 'Missing information',
      description: message,
      type: 'info',
    });

    let target = null;
    errorKeys.forEach((key) => {
      const node = findNode(key);
      if (
        node &&
        (!target || node.getBoundingClientRect().top < target.getBoundingClientRect().top)
      ) {
        target = node;
      }
    });

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount, isSubmitting, errors]);
};

export default useScrollToFirstError;
